import React, { useRef, useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { QRCodeCanvas } from "qrcode.react";
import { toPng } from "html-to-image";
import { Download, Share2, Copy, Loader2, Eye, Users, PawPrint } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Avatar } from "./Avatar";

export function ShareCard({ user, open, onClose }) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState(null);

  const handle = user?.vanity || user?.username || user?.id;
  const shareUrl = `${window.location.origin}/${handle}`;

  useEffect(() => {
    if (open && user?.id) {
      api.get(`/users/${user.id}/stats`).then(({ data }) => setStats(data)).catch(() => setStats(null));
    }
  }, [open, user?.id]);

  const render = async () => {
    // Fonts/images need a beat to settle before rasterizing.
    await new Promise((r) => setTimeout(r, 120));
    return toPng(cardRef.current, { cacheBust: true, pixelRatio: 2, backgroundColor: "#2a0e12" });
  };

  const download = async () => {
    setBusy(true);
    try {
      const dataUrl = await render();
      const a = document.createElement("a");
      a.download = `spotter-${handle}.png`;
      a.href = dataUrl;
      a.click();
      toast.success("Card saved!");
    } catch (e) { toast.error("Couldn't build the card. Try again."); }
    finally { setBusy(false); }
  };

  const share = async () => {
    setBusy(true);
    try {
      const dataUrl = await render();
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], `spotter-${handle}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "My Spotter", text: `Find me on Spotter — ${shareUrl}` });
      } else if (navigator.share) {
        await navigator.share({ title: "My Spotter", text: `Find me on Spotter — ${shareUrl}`, url: shareUrl });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Link copied — paste it anywhere!");
      }
    } catch (e) { if (e.name !== "AbortError") toast.error("Sharing not available on this device."); }
    finally { setBusy(false); }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied!"); }
    catch (e) { toast.error("Couldn't copy link."); }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm bg-spotter-cream border-2 border-black p-4" data-testid="share-card-dialog">
        <DialogTitle className="font-display text-xl text-spotter-maroon text-center">Share your Spotter</DialogTitle>

        {/* The exported card */}
        <div ref={cardRef} data-testid="share-card-canvas"
          className="rounded-2xl overflow-hidden border-4 border-spotter-gold"
          style={{ background: "linear-gradient(160deg,#3a1218 0%,#2a0e12 55%,#1c090c 100%)" }}>
          <div className="p-5 flex flex-col items-center text-center">
            <div className="text-spotter-gold font-display tracking-[0.2em] text-sm">🐆 Spotter 🐆</div>
            <Avatar user={user} size={84} className="my-9" />
            <div className="text-2xl font-display font-bold text-white leading-tight">{user.name}</div>
            <div className="text-spotter-gold/90 text-sm font-heading">@{user.username}</div>
            {user.premium_tier && (
              <div className="mt-1 inline-block text-[10px] font-bold tracking-wide uppercase bg-spotter-gold text-spotter-maroon rounded-full px-2 py-0.5">
                {user.premium_tier} member
              </div>
            )}
            {user.bio && <p className="mt-2 text-white/80 text-xs line-clamp-2 max-w-[240px]">{user.bio}</p>}

            <div className="mt-3 flex items-center justify-center gap-4 text-white/90 text-xs">
              <div className="flex flex-col items-center">
                <div className="flex items-center gap-1 font-bold text-sm"><Users className="w-3.5 h-3.5 text-spotter-gold" />{stats?.pride_count ?? "—"}</div>
                <span className="text-white/50 text-[10px] uppercase tracking-wide">Pride</span>
              </div>
              <div className="flex flex-col items-center">
                <div className="flex items-center gap-1 font-bold text-sm"><PawPrint className="w-3.5 h-3.5 text-spotter-gold" />{stats?.paws_received ?? "—"}</div>
                <span className="text-white/50 text-[10px] uppercase tracking-wide">Paws</span>
              </div>
              <div className="flex flex-col items-center">
                <div className="flex items-center gap-1 font-bold text-sm"><Eye className="w-3.5 h-3.5 text-spotter-gold" />{user.visit_count || 0}</div>
                <span className="text-white/50 text-[10px] uppercase tracking-wide">Views</span>
              </div>
            </div>

            <div className="mt-4 bg-white rounded-lg p-2 border-2 border-spotter-gold">
              <QRCodeCanvas value={shareUrl} size={116} bgColor="#ffffff" fgColor="#2a0e12" level="M" includeMargin={false} />
            </div>
            <div className="mt-2 text-white text-xs font-bold break-all max-w-[240px]">{shareUrl.replace(/^https?:\/\//, "")}</div>
            <div className="mt-1 text-white/50 text-[10px]">Scan to visit my profile</div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 mt-1">
          <Button data-testid="share-card-download" onClick={download} disabled={busy}
            className="bg-spotter-maroon text-spotter-cream border-2 border-black gap-1 text-xs">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Save
          </Button>
          <Button data-testid="share-card-share" onClick={share} disabled={busy}
            className="bg-spotter-gold text-spotter-maroon border-2 border-black gap-1 text-xs">
            <Share2 className="w-4 h-4" /> Share
          </Button>
          <Button data-testid="share-card-copy" onClick={copyLink} variant="outline"
            className="border-2 border-black gap-1 text-xs">
            <Copy className="w-4 h-4" /> Link
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
